
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import AgentSalesMetricTableFilterable from '@/components/reports-v3/components/filterables/AgentSalesMetricTableFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { COL, getTableName } from '@/worker/db/sqlHelpers';
import {
	PermissionsGroup,
	AgentSalesMetrics,
} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import DateFilter from '@/components/reports-v3/components/filters/DateFilter.vue';
import moment from 'moment';
import IncludeTaxToggle from '@/components/IncludeTaxToggle.vue';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';

@Component({
	components: {
        IncludeDebitNoteToggle,
		AgentSalesMetricTableFilterable,
		DateFilter,
		IncludeTaxToggle,
		IncludeCreditNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class SalesMetricsTable extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['sales', 'quotations'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
		| 'compareDate1'
		| 'compareDate2'
	> = ['stockItems', 'agents', 'compareDate1', 'compareDate2'];

	public year1Result = {} as AgentSalesMetrics;
	public year2Result = {} as AgentSalesMetrics;

	public selectedDate1 = moment().add(-1, 'year').startOf('year').valueOf();
	public selectedDate2 = moment().startOf('year').valueOf();

	public get includeTax() {
		return settingx.now.includeTax;
	}

	public get numDecimal() {
		return gdbx.numDecimal;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			includeTax,
			selectedDate1,
			selectedDate2,
			selectedStockItems,
			selectedAgents,
			includeCreditNote,
            includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			includeTax,
			selectedDate1,
			selectedDate2,
			selectedStockItems,
			selectedAgents,
			includeCreditNote,
            includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.5);

		const queryHead = await this._ensureTablesLoaded(
			['sales', 'creditNotes', 'customerTrades', 'quotations'] as any[],
		);
		this.checkInterrupted(jobId);

		const salesTable = getTableName('sales');
		const cnTable = getTableName('creditNotes');
		const arTable = getTableName('customerTrades');
		const quotationsTable = getTableName('quotations');

		const mmt = moment();
		const currentMonth = mmt.month() + 1;
		const currentYear = mmt.year();

		const year1 = moment(this.selectedDate1).get('year');
		const year2 = moment(this.selectedDate2).get('year');

		const y1Start = moment([year1]).startOf('year').valueOf();
		const y1End = moment([year1]).endOf('year').valueOf();
		const y2Start = moment([year2]).startOf('year').valueOf();
		const y2End = moment([year2]).endOf('year').valueOf();
		const y1DateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${y1Start} AND ${y1End}`;
		const y2DateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${y2Start} AND ${y2End}`;

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const allAgentLen = (gdbx.allAgents && gdbx.allAgents.length) || 0;
		const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
			&& this.selectedAgents.length !== allAgentLen
			? ` AND ${COL.AGENT} IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
			: '';
		const allStockItemsLen = (gdbx.allStockItems && gdbx.allStockItems.length) || 0;
		const stockItemFilter = this.selectedStockItems && this.selectedStockItems.length > 0
			&& this.selectedStockItems.length !== allStockItemsLen
			? ` AND ${COL.ITEMCODE} IN (${this.selectedStockItems.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';

		let year1SalesAmount = 0;
		let year2SalesAmount = 0;
		let year1SalesDocQty = 0;
		let year2SalesDocQty = 0;
		let year1SalesDocDateQty = 0;
		let year2SalesDocDateQty = 0;
		let year1CNAmount = 0;
		let year2CNAmount = 0;
		let year1CNCases = 0;
		let year2CNCases = 0;

		if (this.slPhModuleOnly) {
			const amtExpr = this.includeTax
				? '(TRY_CAST("AMOUNT" AS DOUBLE) + COALESCE(TRY_CAST("TAX" AS DOUBLE), 0))'
				: 'TRY_CAST("AMOUNT" AS DOUBLE)';

			const docTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
			const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${docTypes.map((t) => `'${t}'`).join(', ')})`;

			const y1SalesFilter = `${y1DateFilter}${stockItemFilter}${docTypeFilter}${agentFilter}`;
			const y2SalesFilter = `${y2DateFilter}${stockItemFilter}${docTypeFilter}${agentFilter}`;
			const y1CnFilter = `${y1DateFilter}${stockItemFilter}${agentFilter}`;
			const y2CnFilter = `${y2DateFilter}${stockItemFilter}${agentFilter}`;

			const salesQuery = (filter: string) => `
				SELECT COUNT(DISTINCT "DOCNO") as docCount,
					COUNT(DISTINCT "DOCDATE") as dateCount,
					SUM(${amtExpr}) as totalAmt
				FROM ${salesTable}
				WHERE ${filter}
			`;
			const cnQuery = (filter: string) => `
				SELECT COUNT(DISTINCT "DOCNO") as docCount,
					SUM(${amtExpr}) as totalAmt
				FROM ${cnTable}
				WHERE ${filter}
			`;

			const [sY1, sY2, cY1, cY2] = await Promise.all([
				queryHead.rawQuery(salesQuery(y1SalesFilter)),
				queryHead.rawQuery(salesQuery(y2SalesFilter)),
				queryHead.rawQuery(cnQuery(y1CnFilter)),
				queryHead.rawQuery(cnQuery(y2CnFilter)),
			]);
			this.checkInterrupted(jobId);

			year1SalesAmount = Number(sY1[0] && sY1[0].totalAmt) || 0;
			year2SalesAmount = Number(sY2[0] && sY2[0].totalAmt) || 0;
			year1SalesDocQty = Number(sY1[0] && sY1[0].docCount) || 0;
			year2SalesDocQty = Number(sY2[0] && sY2[0].docCount) || 0;
			year1SalesDocDateQty = Number(sY1[0] && sY1[0].dateCount) || 0;
			year2SalesDocDateQty = Number(sY2[0] && sY2[0].dateCount) || 0;
			year1CNAmount = Number(cY1[0] && cY1[0].totalAmt) || 0;
			year2CNAmount = Number(cY2[0] && cY2[0].totalAmt) || 0;
			year1CNCases = Number(cY1[0] && cY1[0].docCount) || 0;
			year2CNCases = Number(cY2[0] && cY2[0].docCount) || 0;
		} else {
			const arAmtExpr = this.includeTax
				? '(TRY_CAST("LOCALAMOUNT" AS DOUBLE) + COALESCE(TRY_CAST("LOCALTAXAMT" AS DOUBLE), 0))'
				: 'TRY_CAST("LOCALAMOUNT" AS DOUBLE)';

			const salesDocTypes = this.includeDebitNote ? `('IV', 'DN')` : `('IV')`;

			const y1ArFilter = `${y1DateFilter}${agentFilter}`;
			const y2ArFilter = `${y2DateFilter}${agentFilter}`;

			const arMainQuery = (filter: string) => `
				SELECT
					SUM(CASE WHEN "DOCTYPE" = 'IV' THEN ${arAmtExpr} ELSE 0 END) as ivAmt,
					SUM(CASE WHEN "DOCTYPE" = 'DN' THEN ${arAmtExpr} ELSE 0 END) as dnAmt,
					SUM(CASE WHEN "DOCTYPE" = 'CN' THEN ${arAmtExpr} ELSE 0 END) as cnAmt,
					COUNT(DISTINCT CASE WHEN "DOCTYPE" IN ${salesDocTypes} THEN "DOCDATE" END) as salesDateCount
				FROM ${arTable}
				WHERE ${filter}
			`;

			const arRefsQuery = (filter: string) => `
				SELECT "DOCTYPE" as docType, COUNT(DISTINCT ref) as refCount
				FROM (
					SELECT "DOCTYPE", UNNEST(string_split("DOCREFS", '|')) as ref
					FROM ${arTable}
					WHERE ${filter}
				) sub
				WHERE ref IS NOT NULL AND TRIM(ref) != ''
				GROUP BY "DOCTYPE"
			`;

			const [arY1Main, arY1Refs, arY2Main, arY2Refs] = await Promise.all([
				queryHead.rawQuery(arMainQuery(y1ArFilter)),
				queryHead.rawQuery(arRefsQuery(y1ArFilter)),
				queryHead.rawQuery(arMainQuery(y2ArFilter)),
				queryHead.rawQuery(arRefsQuery(y2ArFilter)),
			]);
			this.checkInterrupted(jobId);

			const getRefCount = (rows: any[], docType: string): number => {
				const row = rows.find((r: any) => String(r.docType) === docType);
				return row ? (Number(row.refCount) || 0) : 0;
			};

			year1SalesAmount = (Number(arY1Main[0] && arY1Main[0].ivAmt) || 0)
				+ (this.includeDebitNote ? (Number(arY1Main[0] && arY1Main[0].dnAmt) || 0) : 0);
			year2SalesAmount = (Number(arY2Main[0] && arY2Main[0].ivAmt) || 0)
				+ (this.includeDebitNote ? (Number(arY2Main[0] && arY2Main[0].dnAmt) || 0) : 0);
			year1CNAmount = Number(arY1Main[0] && arY1Main[0].cnAmt) || 0;
			year2CNAmount = Number(arY2Main[0] && arY2Main[0].cnAmt) || 0;

			year1SalesDocQty = getRefCount(arY1Refs, 'IV')
				+ (this.includeDebitNote ? getRefCount(arY1Refs, 'DN') : 0);
			year2SalesDocQty = getRefCount(arY2Refs, 'IV')
				+ (this.includeDebitNote ? getRefCount(arY2Refs, 'DN') : 0);
			year1CNCases = getRefCount(arY1Refs, 'CN');
			year2CNCases = getRefCount(arY2Refs, 'CN');

			year1SalesDocDateQty = Number(arY1Main[0] && arY1Main[0].salesDateCount) || 0;
			year2SalesDocDateQty = Number(arY2Main[0] && arY2Main[0].salesDateCount) || 0;
		}

		// Net sales = Sales - CN (if toggle on)
		const year1NetSales = year1SalesAmount - (this.includeCreditNote ? year1CNAmount : 0);
		const year2NetSales = year2SalesAmount - (this.includeCreditNote ? year2CNAmount : 0);

		// Net cases = Cases - CN cases (if toggle on)
		const year1NetCases = year1SalesDocQty - (this.includeCreditNote ? year1CNCases : 0);
		const year2NetCases = year2SalesDocQty - (this.includeCreditNote ? year2CNCases : 0);

		const year1AverageSalesPerCase =
			year1NetCases !== 0 ? year1NetSales / year1NetCases : 0;
		const year2AverageSalesPerCase =
			year2NetCases !== 0 ? year2NetSales / year2NetCases : 0;

		const year1AverageSalesCase =
			year1 === currentYear
				? year1NetCases / currentMonth
				: year1NetCases / 12;
		const year2AverageSalesCase =
			year2 === currentYear
				? year2NetCases / currentMonth
				: year2NetCases / 12;

		const year1AverageSales =
			year1 === currentYear
				? year1NetSales / currentMonth
				: year1NetSales / 12;
		const year2AverageSales =
			year2 === currentYear
				? year2NetSales / currentMonth
				: year2NetSales / 12;

		const year1AverageSalesCycle =
			year1SalesDocDateQty !== 0
				? moment().endOf('year').year(year1).dayOfYear() / year1SalesDocDateQty
				: 0;
		const year2AverageSalesCycle =
			year2SalesDocDateQty !== 0
				? moment().endOf('year').year(year2).dayOfYear() / year2SalesDocDateQty
				: 0;

		// Quotation metrics
		const quotAmtExpr = this.includeTax
			? '(TRY_CAST("AMOUNT" AS DOUBLE) + COALESCE(TRY_CAST("TAX" AS DOUBLE), 0))'
			: 'TRY_CAST("AMOUNT" AS DOUBLE)';

		const y1QuotFilter = `${y1DateFilter}${stockItemFilter}${agentFilter}`;
		const y2QuotFilter = `${y2DateFilter}${stockItemFilter}${agentFilter}`;

		const quotMetricsQuery = (filter: string) => `
			SELECT COUNT(DISTINCT "DOCNO") as docCount,
				SUM(${quotAmtExpr}) as totalAmt
			FROM ${quotationsTable}
			WHERE ${filter}
		`;

		const convertedQuery = (filter: string) => `
			SELECT COUNT(*) as convertedCount
			FROM (
				SELECT "DOCNO"
				FROM ${quotationsTable}
				WHERE ${filter}
				GROUP BY "DOCNO"
				HAVING SUM(TRY_CAST("AMOUNT" AS DOUBLE) - TRY_CAST("OUTSTANDINGAMOUNT" AS DOUBLE)) != 0
			) sub
		`;

		const [qY1Metrics, qY2Metrics, qY1Conv, qY2Conv] = await Promise.all([
			queryHead.rawQuery(quotMetricsQuery(y1QuotFilter)),
			queryHead.rawQuery(quotMetricsQuery(y2QuotFilter)),
			queryHead.rawQuery(convertedQuery(y1QuotFilter)),
			queryHead.rawQuery(convertedQuery(y2QuotFilter)),
		]);
		this.checkInterrupted(jobId);

		this.generateLoadingText(0.9, 0.95);

		const year1QuotationDocQty = Number(qY1Metrics[0] && qY1Metrics[0].docCount) || 0;
		const year2QuotationDocQty = Number(qY2Metrics[0] && qY2Metrics[0].docCount) || 0;
		const year1QuotationAmount = Number(qY1Metrics[0] && qY1Metrics[0].totalAmt) || 0;
		const year2QuotationAmount = Number(qY2Metrics[0] && qY2Metrics[0].totalAmt) || 0;
		const year1ConvertedQuotationCaseQty = Number(qY1Conv[0] && qY1Conv[0].convertedCount) || 0;
		const year2ConvertedQuotationCaseQty = Number(qY2Conv[0] && qY2Conv[0].convertedCount) || 0;

		const year1AverageQuotationCase =
			year1 === currentYear
				? year1QuotationDocQty / currentMonth
				: year1QuotationDocQty / 12;
		const year2AverageQuotationCase =
			year2 === currentYear
				? year2QuotationDocQty / currentMonth
				: year2QuotationDocQty / 12;

		const year1WinRate =
			year1QuotationDocQty !== 0
				? (year1ConvertedQuotationCaseQty / year1QuotationDocQty) * 100
				: 0;
		const year2WinRate =
			year2QuotationDocQty !== 0
				? (year2ConvertedQuotationCaseQty / year2QuotationDocQty) * 100
				: 0;

		this.year1Result = {
			year: year1,
			salesAmount: year1NetSales,
			salesDocQty: year1NetCases,
			averageSalesPerCase: year1AverageSalesPerCase,
			averageSalesCase: year1AverageSalesCase,
			averageSales: year1AverageSales,
			averageSalesCycle: year1AverageSalesCycle,
			quotationAmount: year1QuotationAmount,
			quotationDocQty: year1QuotationDocQty,
			averageQuotationCase: year1AverageQuotationCase,
			winRate: year1WinRate,
		};

		this.year2Result = {
			year: year2,
			salesAmount: year2NetSales,
			salesDocQty: year2NetCases,
			averageSalesPerCase: year2AverageSalesPerCase,
			averageSalesCase: year2AverageSalesCase,
			averageSales: year2AverageSales,
			averageSalesCycle: year2AverageSalesCycle,
			quotationAmount: year2QuotationAmount,
			quotationDocQty: year2QuotationDocQty,
			averageQuotationCase: year2AverageQuotationCase,
			winRate: year2WinRate,
		};

		this.saveHistory('year1Result', 'year2Result');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
